import { theme, Typography, Tag, Modal, Form, Flex, Input } from 'antd';
import {
  Printer,
  FileText,
  FileSpreadsheet,
  BellRing,
  CircleSlash,
  Clock9,
  CircleCheckBig,
  CircleAlert,
} from 'lucide-react';
import {
  type ReactNode,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
} from 'react';
import { tap, catchError, EMPTY, finalize, from, switchMap, throwError, of } from 'rxjs';
import { getRequestParamsForGetInvoicesRequest, renderSitesColumn } from './invoices.helper';
import { type InvoicesExpandedRowState, type InvoiceExpandedRowProps } from './types';
import { type DataTableProps, DataTable } from '../../../components/data-table';
import { AuthContext } from '../../../contexts/auth-context-provider';
import { type InvoiceDto } from '../../../dto/billing/InvoiceDto';
import { type ResponseDto } from '../../../dto/ResponseDto';
import { type CustomerMonthInvoice, type Invoice, type SiteInvoice } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApi from '../../../hooks/use-secured-api';
import useSecuredApiV2 from '../../../hooks/use-secured-api-v2';
import { Roles } from '../../../types';
import { CUSTOMER_MONTH_INVOICE_URL, EXPORT_TO_EXCEL_URL } from '../../../utils/endpoints';
import utils from '../../../utils/utils';

const MARK_AS_UNPAID_SUCCESSFULLY = 'Invoice has been marked as unpaid successfully';
const MARK_AS_PENDING_SUCCESSFULLY = 'Invoice has been marked as pending successfully';
const MARK_AS_PAID_SUCCESSFULLY = 'Invoice has been marked as paid successfully';
const NOTIFY_RECIPIENTS_SUCCESSFULLY = 'Recipients are notified successfully';

const reducer = (
  state: Readonly<InvoicesExpandedRowState>,
  action: Readonly<{
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?: InvoicesExpandedRowState['filters'];
  }>
): InvoicesExpandedRowState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload,
      };
    default:
      return {
        ...state,
      };
  }
};

export const InvoiceExpandedRow = (props: InvoiceExpandedRowProps): ReactNode => {
  const notification = useNotification();
  const { session } = useContext(AuthContext);
  const { token } = theme.useToken();
  const api = useSecuredApi();
  const apiV2 = useSecuredApiV2();
  const { customerMonthInvoiceId, filters, fetchInvoice, displayInvoice, apiClient, onUpdate } =
    props;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [invoicesResponse, setInvoicesResponse] = useState<ResponseDto<InvoiceDto[]>>(
    {} as unknown as ResponseDto<InvoiceDto[]>
  );

  const [state, setState] = useReducer(reducer, {
    filters,
  });

  // Listen for change in filters
  useEffect(() => {
    setState({ type: 'SET_FILTER', payload: { ...state, ...filters } });
  }, [filters]);

  const [debouncedState] = useDebouncedState({ state, time: 300 });

  // Sends request getting invoices of certain customerMonthInvoice
  const getInvoicesByCustomerMonthInvoiceId = (
    customerMonthInvoiceId: CustomerMonthInvoice['id']
  ): void => {
    setIsLoading(true);
    const URL = CUSTOMER_MONTH_INVOICE_URL + `/${customerMonthInvoiceId}` + '/invoices';
    apiV2
      .get(URL, getRequestParamsForGetInvoicesRequest(state), {})
      .pipe(
        tap((response) => {
          setInvoicesResponse(response);
        }),
        catchError((_: Error) => {
          notification.error({
            message: 'Failed To load invoices',
          });
          return EMPTY;
        }),
        finalize(() => {
          setIsLoading(false);
        })
      )
      .subscribe();
  };

  useEffect(() => {
    if (!debouncedState.isDebouncing) {
      getInvoicesByCustomerMonthInvoiceId(customerMonthInvoiceId);
    }
  }, [debouncedState]);

  const exportInvoiceAsExcel = useCallback((item: InvoiceDto): void => {
    api
      .get(EXPORT_TO_EXCEL_URL, { invoiceId: item.id }, {}, true)
      .pipe(
        tap(({ blob, filename }: { blob: Blob; filename?: string }) => {
          const url = URL.createObjectURL(blob);
          utils.downloadFile(url, filename ?? `invoice-${item.invoiceNumber}.xlsx`);
          URL.revokeObjectURL(url);
        }),
        catchError((error: Error) => {
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'Could not export the invoice',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  }, []);

  const columns: DataTableProps<InvoiceDto>['columns'] = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      width: 100,
      render: (value: Invoice['invoiceNumber'], invoice) => (
        <Typography.Link
          onClick={() => {
            displayInvoice(invoice.id);
          }}
        >
          <b>#{value}</b>
        </Typography.Link>
      ),
    },

    {
      title: 'Total Price',
      dataIndex: 'totalPriceWithTaxes',
      width: 100,
      render: (value: Invoice['totalPriceWithTaxes']) => utils.currencyFormat(value),
    },
    {
      title: 'Sites',
      dataIndex: ['siteInvoices'],
      width: 100,
      render: (siteInvoices: SiteInvoice[]) => renderSitesColumn(siteInvoices),
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      width: 100,
      render: (value: Invoice['paymentStatus']) =>
        value === 'PAID' ? (
          <Tag color={token.colorPrimary}>PAID</Tag>
        ) : value === 'PENDING' ? (
          <Tag color="processing">PENDING</Tag>
        ) : (
          <Tag color="processing">UNPAID</Tag>
        ),
    },
  ];

  const actionsMenu = useMemo<DataTableProps<InvoiceDto>['actions']>(
    () => [
      {
        key: 'print',
        label: 'Print',
        icon: <Printer />,
        onItemClick: (item) => {
          fetchInvoice(item.id, utils.printFile);
        },
      },
      {
        key: 'export-pdf',
        label: 'Export to PDF',
        icon: <FileText />,
        onItemClick: (item) => {
          fetchInvoice(item.id, (url) => {
            utils.downloadFile(url, `${item.invoiceNumber}.pdf`);
          });
        },
      },
      {
        key: 'export-xlsx',
        label: 'Export to Excel',
        icon: <FileSpreadsheet />,
        onItemClick: (item) => {
          exportInvoiceAsExcel(item);
        },
      },
    ],
    [exportInvoiceAsExcel]
  );
  const adminActionsMenu = useMemo<DataTableProps<InvoiceDto>['actions']>(
    () => [
      { type: 'divider' },
      {
        key: 'notify-recipients',
        label: 'Notify recipients',
        icon: <BellRing />,
        isDisabled: (item) => item.paymentStatus !== 'UNPAID',
        onItemClick: (item) => {
          from(apiClient.mutate(['invoice', item.id], { notificationStatus: 'QUEUED' }))
            .pipe(
              switchMap((res) =>
                res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
              ),
              tap(() => {
                notification.success({
                  message: ' ',
                  description: NOTIFY_RECIPIENTS_SUCCESSFULLY,
                });
              }),
              catchError((error: Error) => {
                notification.error({
                  message: '',
                  description: error.message,
                });
                return EMPTY;
              })
            )
            .subscribe();
        },
      },
      { type: 'divider' },
      {
        key: 'mark-as-unpaid',
        label: 'Mark as unpaid',
        icon: <CircleSlash />,
        isHidden: (item) => !item.canMarkUnpaid,
        onItemClick: (item) => {
          from(
            apiClient.mutate(['invoice', item.id], {
              id: item.id,
              paymentStatus: 'UNPAID',
            })
          )
            .pipe(
              switchMap((res) =>
                res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
              ),
              tap(() => {
                item.paymentStatus = 'UNPAID';
                notification.success({
                  message: ' ',
                  description: MARK_AS_UNPAID_SUCCESSFULLY,
                });
                onUpdate();
              }),
              catchError((error: Error) => {
                notification.error({
                  message: '',
                  description: error.message,
                });
                return EMPTY;
              })
            )
            .subscribe();
        },
      },
      {
        key: 'mark-as-pending',
        label: 'Mark as pending',
        icon: <Clock9 />,
        isHidden: (item) => !item.canMarkPending,
        onItemClick: (item) => {
          from(
            apiClient.mutate(['invoice', item.id], {
              id: item.id,
              paymentStatus: 'PENDING',
            })
          )
            .pipe(
              switchMap((res) =>
                res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
              ),
              tap(() => {
                item.paymentStatus = 'PENDING';
                notification.success({
                  message: ' ',
                  description: MARK_AS_PENDING_SUCCESSFULLY,
                });
                onUpdate();
              }),
              catchError((error: Error) => {
                notification.error({
                  message: '',
                  description: error.message,
                });
                return EMPTY;
              })
            )
            .subscribe();
        },
      },
      {
        key: 'mark-as-paid',
        label: 'Mark as paid',
        icon: <CircleCheckBig />,
        isDisabled: (item) => !item.canMarkPaid,
        onItemClick: (item) => {
          const modal = Modal.confirm({
            width: '50%',
            title: 'Confirm marking invoice as paid',
            icon: <CircleAlert />,
            content: (
              <Form
                id="mark-as-paid-form"
                onFinish={(_values) => {
                  from(
                    apiClient.mutate(['invoice', item.id], {
                      id: item.id,
                      paymentStatus: 'PAID',
                    })
                  )
                    .pipe(
                      switchMap((res) =>
                        res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
                      ),
                      tap(() => {
                        modal.destroy();
                        item.paymentStatus = 'PAID';
                        notification.success({
                          message: ' ',
                          description: MARK_AS_PAID_SUCCESSFULLY,
                        });
                        onUpdate();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: '',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                }}
              >
                <Flex vertical gap="middle">
                  <Typography.Paragraph>
                    Are you sure you want to mark invoice <b>{`#${item.invoiceNumber}`}</b> as paid?
                  </Typography.Paragraph>
                  <Form.Item
                    name="paid"
                    rules={[
                      { required: true, message: "Type 'PAID' to confirm" },
                      { pattern: /PAID/, message: "Type 'PAID' to confirm" },
                    ]}
                  >
                    <Input placeholder="Type 'PAID' to confirm" />
                  </Form.Item>
                </Flex>
              </Form>
            ),
            cancelText: 'Cancel',
            okButtonProps: {
              htmlType: 'submit',
              type: 'primary',
              form: 'mark-as-paid-form',
            },
            okText: 'Confirm',
            onOk: (_args) => false,
          });
        },
      },
    ],
    []
  );

  return (
    <DataTable<InvoiceDto>
      size="small"
      columns={columns}
      dataList={invoicesResponse?.data ?? []}
      description={''}
      onChange={() => {}}
      totalRecords={invoicesResponse?.data?.length ?? 0}
      loading={isLoading}
      title={''}
      actions={actionsMenu?.concat(
        session?.claims.role === Roles.ROLE_HHC_ADMIN ? adminActionsMenu : ([] as any)
      )}
      style={{
        marginBlockStart: token.marginMD * -1,
        marginBlockEnd: token.marginMD,
        marginInline: token.marginLG * -1,
        background: 'transparent',
      }}
    />
  );
};
